/**
 * The bank: every `AudioBuffer` and shaping curve the voices share, baked once at start-up.
 *
 * Nothing here is loaded. Every sample in every buffer is generated on the client from the seeded
 * RNG, which is what "no sampled audio is used" in the credits screen actually means — the whole
 * sound of this simulator is about 40 kB of code and some arithmetic.
 *
 * ## The cost, stated plainly
 *
 * Baking runs once and takes a few milliseconds: about 480 000 samples of white noise, 290 000 of
 * pink, and a dozen short one-shots. It happens while the boot splash is still up, alongside
 * shader compilation, so it is not on any frame's critical path. The buffers are roughly 2 MB of
 * runtime heap — they are *not* payload, since none of it is downloaded.
 *
 * ## Why the one-shots are baked rather than played from nodes
 *
 * A touchdown thump built from live nodes is an oscillator, a gain, a filter, four scheduled
 * automation events and a teardown, every time a wheel touches. Baked, it is one
 * `AudioBufferSourceNode` and one `start()`. The gun fires ten times a second and the difference
 * stops being academic.
 *
 * `updateFixed` / `updateFrame`: neither. Constructed once.
 */

import { Rng } from '../core/rng.js';
import {
  crossfadeLoopSeam,
  fillBirdCall,
  fillImpulse,
  fillPinkNoise,
  fillPulseCurve,
  fillSoftClipCurve,
  fillWhiteNoise,
  seamlessLoopEnd,
} from './noise.js';

/** Seconds of white noise. Long enough that the loop is not a rhythm the ear can latch onto. */
const WHITE_SECONDS = 4;
/** Seconds of pink noise. Longer, because its low-frequency content makes a short loop obvious. */
const PINK_SECONDS = 6;
/** Crossfade length at the pink loop seam [s]. */
const PINK_SEAM_S = 0.25;
/** Samples in a wave-shaper curve. 1024 is inaudibly smooth and fits in a cache line's worth. */
const CURVE_SAMPLES = 1024;
/** How many distinct bird calls to bake. Four is enough that a repeat is never noticed. */
const BIRD_CALLS = 4;

export interface AudioBank {
  /** Flat-spectrum noise, looping. The raw material for wind, rumble, intake and rain. */
  readonly white: AudioBuffer;
  /** Where the white buffer must loop back to. */
  readonly whiteLoopEndS: number;
  /** 1/f noise, looping and seam-crossfaded. The outdoor wind bed. */
  readonly pink: AudioBuffer;
  readonly pinkLoopEndS: number;

  /** Low-frequency body of a wheel arriving on a runway. */
  readonly thump: AudioBuffer;
  /** Rubber being spun up from nothing to ground speed. */
  readonly chirp: AudioBuffer;
  /** A switch, a detent, a button in the menu. */
  readonly click: AudioBuffer;
  /** A mechanical stop: the flap lever reaching a detent, a gear lock engaging. */
  readonly clunk: AudioBuffer;
  /** The transient of a cannon round leaving the barrel. */
  readonly cannon: AudioBuffer;
  /** The body of an explosion, before distance filtering. */
  readonly explosion: AudioBuffer;
  /** Small birds, for a quiet ramp. */
  readonly birds: readonly AudioBuffer[];

  /** `max(0, sin)^k` — turns an oscillator into the blade-pass impulse train. */
  readonly pulseCurve: Float32Array<ArrayBuffer>;
  /** A gentle `tanh` saturator, for the engine's timbre rather than its safety. */
  readonly softClipCurve: Float32Array<ArrayBuffer>;
}

/**
 * Bake the bank.
 *
 * `seed` is forked from the project's global RNG by the caller, so audio never perturbs the
 * sequence any other subsystem is drawing from (non-negotiable #7).
 */
export function createAudioBank(context: BaseAudioContext, seed: Rng): AudioBank {
  const rate = context.sampleRate;

  const white = context.createBuffer(1, Math.floor(WHITE_SECONDS * rate), rate);
  fillWhiteNoise(white.getChannelData(0), seed);

  const pinkSamples = Math.floor(PINK_SECONDS * rate);
  const pink = context.createBuffer(1, pinkSamples, rate);
  const pinkData = pink.getChannelData(0);
  fillPinkNoise(pinkData, seed);
  const seamSamples = Math.floor(PINK_SEAM_S * rate);
  crossfadeLoopSeam(pinkData, seamSamples);

  const pulseCurve = new Float32Array(CURVE_SAMPLES);
  fillPulseCurve(pulseCurve, 6);
  const softClipCurve = new Float32Array(CURVE_SAMPLES);
  fillSoftClipCurve(softClipCurve, 1.8);

  const birds: AudioBuffer[] = [];
  for (let i = 0; i < BIRD_CALLS; i++) {
    const call = context.createBuffer(1, Math.floor(1.1 * rate), rate);
    fillBirdCall(call.getChannelData(0), rate, seed);
    birds.push(call);
  }

  return {
    white,
    whiteLoopEndS: white.duration,
    pink,
    pinkLoopEndS: seamlessLoopEnd(pinkSamples, seamSamples) / rate,

    /*
     * The one-shots, and why each is the shape it is.
     *
     * The thump is 55 Hz because that is roughly where an aircraft's main gear and the airframe
     * behind it resonate — low enough to be felt, high enough to survive a laptop speaker. It is
     * mostly tone with a little noise: a wheel arriving is a *hit*, not a crash.
     *
     * The chirp is the opposite: almost all noise, an octave and a half up, and short. It is the
     * contact patch skidding for a tenth of a second while the wheel spins up.
     *
     * The click is 1.5 kHz and 12 ms — the smallest sound that still reads as a mechanism rather
     * than a glitch. The clunk is its heavier cousin for a lever hitting its stop.
     *
     * The cannon and the explosion are M6's, built now so the synth is real and can be triggered
     * from the debug handle rather than being a promise. The explosion is long and noise-heavy so
     * that the distance lowpass has something to take away.
     */
    thump: bake(context, seed, 0.6, 55, 0.18, 0.25),
    chirp: bake(context, seed, 0.35, 900, 0.09, 0.9),
    click: bake(context, seed, 0.05, 1_500, 0.012, 0.75),
    clunk: bake(context, seed, 0.25, 220, 0.07, 0.55),
    cannon: bake(context, seed, 0.4, 130, 0.09, 0.7),
    explosion: bake(context, seed, 2.2, 48, 0.75, 0.85),
    birds,

    pulseCurve,
    softClipCurve,
  };
}

function bake(
  context: BaseAudioContext,
  rng: Rng,
  durationS: number,
  frequencyHz: number,
  decayS: number,
  noiseMix: number,
): AudioBuffer {
  const buffer = context.createBuffer(1, Math.max(1, Math.floor(durationS * context.sampleRate)), context.sampleRate);
  fillImpulse(buffer.getChannelData(0), context.sampleRate, frequencyHz, decayS, noiseMix, rng);
  return buffer;
}
